// Janela de reset ("Altar do Renascimento"): bônus atual, trilha dos 10 resets, o que mantém/o que volta e o reset (com confirmação).
// Visual de fênix/ascensão: dourado, brasa e roxo. CSS em client/css-parts/reset.css (prefixo .rs-).
import { useState } from 'preact/hooks';
import { MAX_RESETS, RESET_LEVEL, RESET_STAT_PER, RESET_XP_PER, resetBonusText, resetRequirement, resetStatPct, resetXpPct } from '../../../../shared/src/game/reset';

const pct = (v: number) => String(v).replace('.', ',');
const n = (v: number) => v.toLocaleString('pt-BR');

export function Reset(props: { level: number; resets: number; inCity: boolean; onReset: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [help, setHelp] = useState(false);
  const r = Math.max(0, Math.min(MAX_RESETS, props.resets)), done = r >= MAX_RESETS;
  const need = resetRequirement(r);
  const ready = !done && props.level >= need, missing = Math.max(0, need - props.level);
  // progresso até o próximo reset, contado desde o level base
  const prog = done ? 100 : Math.max(0, Math.min(100, ((props.level - RESET_LEVEL) / (need - RESET_LEVEL)) * 100));
  const ring = (r / MAX_RESETS) * 360;

  return (
    <div class={'rs' + (ready ? ' rs-ready' : '') + (done ? ' rs-done' : '')}>
      <div class="rs-head">
        <div class="rs-emblem" style={`--rs-ring: ${ring}deg`} title={`${r} de ${MAX_RESETS} resets`}>
          <span class="rs-phoenix">🔥</span>
          <b>{r}<small>/{MAX_RESETS}</small></b>
        </div>
        <div class="rs-head-txt">
          <div class="rs-title">Altar do Renascimento
            <button class="rs-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="rs-sub">Renasça no level {RESET_LEVEL} mais forte, sem perder nada do que conquistou.</div>
          <div class="rs-bar" title={done ? 'Todos os resets feitos' : `Level ${props.level} de ${need}`}>
            <i style={{ width: `${prog}%` }} />
            <span>{done ? 'Ascensão completa' : ready ? `Level ${n(props.level)} · reset ${r + 1} liberado!` : `Level ${n(props.level)} / ${n(need)}`}</span>
          </div>
        </div>
      </div>

      {help && (
        <div class="rs-steps">
          <div><i>⚔️</i><b>1. Suba de level</b><small>O 1º reset pede level {resetRequirement(0)}; cada seguinte pede {resetRequirement(1) - resetRequirement(0)} levels a mais (até o level {resetRequirement(MAX_RESETS - 1)}).</small></div>
          <div><i>🔥</i><b>2. Renasça</b><small>Clique em “Renascer” e confirme. Se não estiver no templo, você é levado para lá na hora e volta ao level {RESET_LEVEL}.</small></div>
          <div><i>✨</i><b>3. Fique mais forte</b><small>Cada reset dá +{RESET_XP_PER}% de experiência e +{pct(RESET_STAT_PER)}% de vida, mana e dano, para sempre. É opcional: resete quando quiser.</small></div>
        </div>
      )}

      <div class="rs-stats">
        <div><small>Resets feitos</small><b>{r}<em>/{MAX_RESETS}</em></b></div>
        <div class="rs-gold"><small>Bônus atual</small><b>+{resetXpPct(r)}%<em> XP</em></b><span>+{pct(resetStatPct(r))}% vida, mana e dano</span></div>
        <div><small>{done ? 'Level' : 'Level exigido'}</small><b>{done ? '∞' : n(need)}</b><span>{done ? 'sobe sem limite' : `você está no ${n(props.level)}`}</span></div>
        <div class={ready ? 'rs-ok' : ''}><small>{done ? 'Status' : 'Falta'}</small><b>{done ? '👑' : ready ? 'Pronto!' : n(missing)}</b><span>{done ? 'máximo atingido' : ready ? 'pode renascer' : missing === 1 ? 'level' : 'levels'}</span></div>
      </div>

      <div class="rs-sec">Trilha da ascensão <small>total acumulado em cada etapa</small></div>
      <div class="rs-trail">
        {Array.from({ length: MAX_RESETS }, (_, i) => {
          const st = i < r ? 'won' : i === r ? 'next' : 'lock';
          return (
            <div key={i} class={'rs-node ' + st} title={`Reset ${i + 1}: level ${resetRequirement(i)} · total ${resetBonusText(i + 1)}`}>
              <span class="rs-orb">{st === 'won' ? '✔' : st === 'lock' ? '🔒' : i + 1}</span>
              <b>Lv {resetRequirement(i)}</b>
              <small>+{resetXpPct(i + 1)}% XP</small>
              <small>+{pct(resetStatPct(i + 1))}% atrib.</small>
            </div>
          );
        })}
      </div>

      <div class="rs-cols">
        <div class="rs-keep">
          <div class="rs-col-t">🛡️ Você mantém</div>
          <ul>
            <li>Itens equipados, na mochila e no depot</li>
            <li>Todas as skills</li>
            <li>O bônus de todos os resets anteriores</li>
          </ul>
        </div>
        <div class="rs-back">
          <div class="rs-col-t">↺ O que volta</div>
          <ul>
            <li>Level e experiência voltam ao level {RESET_LEVEL}</li>
            <li>Vida e mana recalculadas (e cheias) para o level {RESET_LEVEL}</li>
            <li>Fora do templo: você é levado para lá e a caça automática para</li>
          </ul>
        </div>
      </div>

      {done ? (
        <div class="rs-cta rs-cta-done">
          <span class="rs-crown">👑</span>
          <div><b>Ascensão completa!</b><small>Você fez os {MAX_RESETS} resets ({resetBonusText(MAX_RESETS)}). Agora o level sobe sem limite.</small></div>
        </div>
      ) : ready ? (
        <div class="rs-cta rs-cta-on">
          <div class="rs-cta-txt">
            <b>O reset {r + 1} está disponível!</b>
            <small>Depois dele: <span class="rs-y">{resetBonusText(r + 1)}</span>.</small>
          </div>
          {!confirm ? (
            <button class="tb-button green rs-go" onClick={() => setConfirm(true)}>🔥 Renascer agora</button>
          ) : (
            <div class="rs-confirm">
              <b>Voltar ao level {RESET_LEVEL} agora{props.inCity ? '' : ' (você vai para o templo)'}?</b>
              <span>
                <button class="tb-button green" onClick={() => { props.onReset(); setConfirm(false); }}>Sim, resetar</button>
                <button class="tb-button" onClick={() => setConfirm(false)}>Cancelar</button>
              </span>
            </div>
          )}
        </div>
      ) : (
        <div class="rs-cta rs-cta-lock">
          <span class="rs-lockico">🔒</span>
          <div class="rs-cta-txt">
            <b>Reset {r + 1} bloqueado</b>
            <small>Pede level {n(need)} — você está no {n(props.level)}. Faltam <span class="rs-y">{n(missing)} {missing === 1 ? 'level' : 'levels'}</span>.</small>
          </div>
          <button class="tb-button" disabled>Precisa do level {n(need)}</button>
        </div>
      )}
    </div>
  );
}
